import { useMemo } from 'react';
import QRCode from 'qrcode';

/** Quiet zone around the code, in modules (ISO/IEC 18004 asks for four). */
const QUIET = 4;

/** The module matrix, at error-correction level M. */
function matrix(value: string) {
  return QRCode.create(value, { errorCorrectionLevel: 'M' }).modules;
}

/**
 * A QR code as inline SVG, drawn from the module matrix: no image request for the code itself (the
 * CSP allows images only from the application's own origin) and no injected markup.
 */
export function QrCode({
  value,
  label,
  size = 200,
}: {
  value: string;
  /** What the code is for, read out in place of the image. */
  label: string;
  /** About this many pixels wide: rounded to a whole number of pixels per module. */
  size?: number;
}) {
  const { modules, path } = useMemo(() => {
    const grid = matrix(value);
    let d = '';
    for (let row = 0; row < grid.size; row++) {
      for (let col = 0; col < grid.size; col++) {
        if (grid.get(row, col) !== 0) {
          d += `M${String(col + QUIET)} ${String(row + QUIET)}h1v1h-1z`;
        }
      }
    }
    return { modules: grid.size + 2 * QUIET, path: d };
  }, [value]);
  // A whole number of pixels per module (at least two) keeps every module's edges sharp on screen,
  // so the drawn size is the nearest such multiple to `size`.
  const pixels = modules * Math.max(2, Math.round(size / modules));
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${String(modules)} ${String(modules)}`}
      width={pixels}
      height={pixels}
      shapeRendering="crispEdges"
    >
      <rect width={modules} height={modules} fill="#fff" />
      <path d={path} fill="#000" />
    </svg>
  );
}

/**
 * The same code as a PNG (a data: URL) for download, about `pixels` wide. Every module is a whole
 * number of pixels, so its edges stay sharp for a scanner; the image is therefore at most one
 * module narrower than `pixels`.
 */
export function qrPng(value: string, pixels = 1024): string {
  const grid = matrix(value);
  const scale = Math.max(1, Math.floor(pixels / (grid.size + 2 * QUIET)));
  const side = scale * (grid.size + 2 * QUIET);
  const canvas = document.createElement('canvas');
  canvas.width = side;
  canvas.height = side;
  const context = canvas.getContext('2d');
  if (context === null) throw new Error('This browser cannot draw the QR code image.');
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, side, side);
  context.fillStyle = '#000000';
  for (let row = 0; row < grid.size; row++) {
    for (let col = 0; col < grid.size; col++) {
      if (grid.get(row, col) !== 0) {
        context.fillRect((col + QUIET) * scale, (row + QUIET) * scale, scale, scale);
      }
    }
  }
  return canvas.toDataURL('image/png');
}
